import { apiFetch, apiDownload } from "./client";
import type { ApiResponse } from "@/types/api";

export interface Empresa {
  id: number;
  razon_social: string;
  nit: string;
  activa: boolean;
  direccion?: string;
  telefono?: string;
  email_contacto?: string;
  persona_contacto?: string;
  observaciones?: string;
  config_scanner?: ScannerConfig;
  created_at?: string;
  updated_at?: string;
}

export interface ScannerConfig {
  modelo_ia?: string;
  umbral_confianza?: number;
  activar_heuristica?: boolean;
  temperature?: number;
}

export interface CreateEmpresaRequest {
  razon_social: string;
  nit: string;
  activa?: boolean;
  direccion?: string;
  telefono?: string;
  email_contacto?: string;
  persona_contacto?: string;
  observaciones?: string;
  config_scanner?: ScannerConfig;
}

export type UpdateEmpresaRequest = CreateEmpresaRequest;

export interface EmpresaUsuario {
  usuario_id: number;
  rol_empresa: "admin_empresa" | "operador" | "lector";
}

export interface EmpresaUsuariosRequest {
  usuarios: EmpresaUsuario[];
}

export interface PucCuenta {
  id: number;
  codigo_puc: string;
  nombre_cuenta: string;
  porcentaje_retencion?: number;
  naturaleza?: "C" | "D";
}

export interface CreatePucRequest {
  codigo_puc: string;
  nombre_cuenta: string;
  porcentaje_retencion?: number;
}

export const listEmpresas = (
  params: { page?: number; limit?: number; q?: string } = {},
) =>
  apiFetch<ApiResponse<Empresa[]> & {
    pagination?: { page: number; limit: number; total: number; total_pages: number };
  }>("/api/v1/empresas", {
    params: { _t: Date.now(), ...params },
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  }).then((res) => ({
    data: res.data ?? [],
    pagination: res.pagination,
  }));

export const getEmpresa = (id: number) =>
  apiFetch<ApiResponse<Empresa>>(`/api/v1/empresas/${id}`).then(
    (res) => res.data as Empresa,
  );

export const createEmpresa = (body: CreateEmpresaRequest) =>
  apiFetch<ApiResponse<null> & { id?: number }>("/api/v1/empresas", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateEmpresa = (id: number, body: UpdateEmpresaRequest) =>
  apiFetch<ApiResponse<null>>(`/api/v1/empresas/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deleteEmpresa = (id: number) =>
  apiFetch<ApiResponse<null>>(`/api/v1/empresas/${id}`, {
    method: "DELETE",
  });

export const listUsuariosDeEmpresa = (empresaId: number) =>
  apiFetch<ApiResponse<EmpresaUsuario[]>>(
    `/api/v1/empresas/${empresaId}/usuarios`,
  ).then((res) => res.data ?? []);

export const updateUsuariosDeEmpresa = (
  empresaId: number,
  usuarios: EmpresaUsuario[],
) =>
  apiFetch<ApiResponse<null>>(`/api/v1/empresas/${empresaId}/usuarios`, {
    method: "PUT",
    body: JSON.stringify({ usuarios }),
  });

type PucTipo = "" | "_ventas" | "_exterior" | "_legalizacion";

const pucBase = (empresaId: number, tipo: PucTipo) =>
  `/api/v1/empresas/${empresaId}/puc${tipo}`;

export const listPuc = (empresaId: number, tipo: PucTipo = "") =>
  apiFetch<ApiResponse<PucCuenta[]>>(pucBase(empresaId, tipo)).then(
    (res) => res.data ?? [],
  );

export const createPuc = (
  empresaId: number,
  body: CreatePucRequest,
  tipo: PucTipo = "",
) =>
  apiFetch<ApiResponse<null>>(pucBase(empresaId, tipo), {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updatePuc = (
  empresaId: number,
  pucId: number,
  body: CreatePucRequest,
  tipo: PucTipo = "",
) =>
  apiFetch<ApiResponse<null>>(`${pucBase(empresaId, tipo)}/${pucId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deletePuc = (
  empresaId: number,
  pucId: number,
  tipo: PucTipo = "",
) =>
  apiFetch<ApiResponse<null>>(`${pucBase(empresaId, tipo)}/${pucId}`, {
    method: "DELETE",
  });

export const bulkImportPuc = async (
  empresaId: number,
  text: string,
  tipo: PucTipo = "",
) => {
  const params = new URLSearchParams();
  params.set("text", text);
  return apiFetch<ApiResponse<null>>(
    `${pucBase(empresaId, tipo)}/bulk?${params.toString()}`,
    { method: "POST" },
  );
};

export const downloadPucTemplate = (
  tipo: PucTipo = "",
  filename = "plantilla_puc.xlsx",
) => {
  const path = `/api/v1/empresas/puc${tipo}/template`;
  const separator = tipo === "" ? "" : "/";
  return apiDownload(
    tipo ? `/api/v1/empresas/puc${separator}${tipo.slice(1)}/template` : path,
    undefined,
    filename,
  );
};
